import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IconButton } from "@bloomberg/media-ui-core";
import { Close, Refresh } from "@bloomberg/media.icons";
import type { Lot, NewHolding } from "@bloomberg/octagon-shared";
import styles from "./holdingsDrawer.module.css";
import DrawerShell from "./DrawerShell";
import DrawerTickerHeader from "./DrawerTickerHeader";
import DrawerOverview from "./DrawerOverview";
import DrawerHistoryTable, { type EditFormState } from "./DrawerHistoryTable";
import {
  useCurrentWatchlist,
  useError,
  useIsDrawerOpen,
  useIsLoading,
  useSelectedTicker,
  useSelectedPortfolioId,
  useUserId,
  useWatchlistActions,
} from "~context/WatchlistContext";
import { watchlistClient } from "~config/octagon-client";

function lotDateToFormDate(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function lotToNewHolding(lot: Lot): NewHolding {
  return {
    id: lot.id,
    ticker: lot.ticker,
    shares: lot.shares,
    buyPrice: lot.buyPrice,
    buyDate: lot.buyDate,
  };
}

function parseDateLocal(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);

  return new Date(year, month - 1, day);
}

function editFormToNewHolding(lot: Lot, form: EditFormState): NewHolding {
  return {
    id: lot.id,
    ticker: lot.ticker,
    shares: form.shares !== "" ? Number(form.shares) : lot.shares,
    buyPrice: form.price !== "" ? Number(form.price) : lot.buyPrice,
    buyDate: form.date ? parseDateLocal(form.date) : lot.buyDate,
  };
}

const EMPTY_FORM: EditFormState = { date: "", shares: "", price: "" };

function DrawerLoadingSkeleton({ lotCount }: { readonly lotCount: number }) {
  const rowCount = Math.max(lotCount, 2);

  return (
    <div className={styles.content}>
      <section>
        <h3 className={styles.sectionTitle}>Overview</h3>
        <div className={styles.overviewGrid}>
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className={styles.skeletonBox} />
          ))}
        </div>
      </section>
      <section>
        <h3 className={styles.sectionTitle}>History</h3>
        <div className={styles.skeletonTable}>
          <div className={styles.historyHeaders}>
            <span className={styles.historyHeaderLabel}>Date</span>
            <span className={styles.historyHeaderLabel}>Shares</span>
            <span className={styles.historyHeaderLabel}>Price</span>
          </div>
          {Array.from({ length: rowCount }, (_, i) => (
            <div key={i} className={styles.skeletonRow} />
          ))}
        </div>
      </section>
    </div>
  );
}

export default function HoldingsDrawer() {
  const isOpen = useIsDrawerOpen();
  const selectedTicker = useSelectedTicker();
  const currentWatchlist = useCurrentWatchlist();
  const userId = useUserId();
  const selectedPortfolioId = useSelectedPortfolioId();
  const isLoading = useIsLoading();
  const loadError = useError();
  const { closeDrawer, saveWatchlistChanges, refreshCurrentWatchlist } =
    useWatchlistActions();

  const [editingLotId, setEditingLotId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingDeletionIds, setPendingDeletionIds] = useState<
    ReadonlySet<number>
  >(new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [addForm, setAddForm] = useState<EditFormState>(EMPTY_FORM);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setEditingLotId(null);
      setEditForm(EMPTY_FORM);
      setSaveError(null);
      setPendingDeletionIds(new Set());
      setIsSaving(false);
      setIsAdding(false);
      setAddForm(EMPTY_FORM);
      setIsConfirmingDelete(false);
      setHasValidationErrors(false);
    }
  }, [isOpen]);

  const position = useMemo(() => {
    if (!selectedTicker || !currentWatchlist?.positions) return null;

    return (
      currentWatchlist.positions.find((p) => p.ticker === selectedTicker) ??
      null
    );
  }, [selectedTicker, currentWatchlist?.positions]);

  const handleEditStart = useCallback(
    (lotId: number) => {
      const lot = position?.lots.find((l) => l.id === lotId);

      if (!lot) return;

      setEditingLotId(lotId);
      setEditForm({
        date: lotDateToFormDate(lot.buyDate),
        shares: String(lot.shares),
        price: String(lot.buyPrice),
      });
      setSaveError(null);
      setHasValidationErrors(false);
    },
    [position?.lots]
  );

  const handleFieldChange = useCallback(
    (field: "date" | "shares" | "price", value: string) => {
      setEditForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleDeleteLot = useCallback(() => {
    if (editingLotId === null) return;

    setPendingDeletionIds((prev) => new Set([...prev, editingLotId]));
  }, [editingLotId]);

  const handleCancel = useCallback(() => {
    setEditingLotId(null);
    setEditForm(EMPTY_FORM);
    setSaveError(null);
    setPendingDeletionIds(new Set());
    setHasValidationErrors(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!currentWatchlist || editingLotId === null) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const holdings: NewHolding[] = [];

      for (const pos of currentWatchlist.positions) {
        for (const lot of pos.lots) {
          if (pendingDeletionIds.has(lot.id)) {
            continue;
          } else if (pos.ticker === selectedTicker && lot.id === editingLotId) {
            holdings.push(editFormToNewHolding(lot, editForm));
          } else {
            holdings.push(lotToNewHolding(lot));
          }
        }
      }

      await saveWatchlistChanges({
        name: currentWatchlist.name,
        currency: currentWatchlist.currency,
        isDefault: currentWatchlist.isDefault,
        holdings,
      });

      setEditingLotId(null);
      setEditForm(EMPTY_FORM);
      setPendingDeletionIds(new Set());
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Failed to save changes"
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    currentWatchlist,
    editingLotId,
    editForm,
    pendingDeletionIds,
    selectedTicker,
    saveWatchlistChanges,
  ]);

  const handleStartAdd = useCallback(() => {
    setEditingLotId(null);
    setEditForm(EMPTY_FORM);
    setSaveError(null);
    setPendingDeletionIds(new Set());
    setIsAdding(true);
    setAddForm(EMPTY_FORM);
    setHasValidationErrors(false);
  }, []);

  const handleAddFieldChange = useCallback(
    (field: "date" | "shares" | "price", value: string) => {
      setAddForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleCancelAdd = useCallback(() => {
    setIsAdding(false);
    setAddForm(EMPTY_FORM);
    setSaveError(null);
    setHasValidationErrors(false);
  }, []);

  const handleSaveAdd = useCallback(async () => {
    if (!userId || !selectedPortfolioId || !selectedTicker) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const client = watchlistClient.getInstance();
      const newHolding: NewHolding = {
        ticker: selectedTicker,
        ...(addForm.shares !== "" && { shares: Number(addForm.shares) }),
        ...(addForm.price !== "" && { buyPrice: Number(addForm.price) }),
        ...(addForm.date && { buyDate: parseDateLocal(addForm.date) }),
      };

      await client.addHoldings(userId, selectedPortfolioId, [newHolding]);
      await refreshCurrentWatchlist();

      setIsAdding(false);
      setAddForm(EMPTY_FORM);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Failed to add holding"
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    userId,
    selectedPortfolioId,
    selectedTicker,
    addForm,
    refreshCurrentWatchlist,
  ]);

  const handleStartDelete = useCallback(() => {
    setEditingLotId(null);
    setEditForm(EMPTY_FORM);
    setIsAdding(false);
    setAddForm(EMPTY_FORM);
    setSaveError(null);
    setPendingDeletionIds(new Set());
    setIsConfirmingDelete(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setIsConfirmingDelete(false);
    setSaveError(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!userId || !selectedPortfolioId || !selectedTicker) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const client = watchlistClient.getInstance();

      await client.deleteHoldingsByTicker(
        userId,
        selectedPortfolioId,
        selectedTicker
      );
      await refreshCurrentWatchlist();

      setIsConfirmingDelete(false);
      closeDrawer();
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Failed to delete holdings"
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    userId,
    selectedPortfolioId,
    selectedTicker,
    refreshCurrentWatchlist,
    closeDrawer,
  ]);

  const title = position?.marketData.longName ?? selectedTicker ?? "";
  const price = position?.marketData.price ?? null;
  const currency = position?.marketData.issuedCurrency ?? "USD";
  const percentChange = position?.marketData.percentChange1Day ?? null;

  const subtitle = (
    <DrawerTickerHeader
      price={price}
      currency={currency}
      percentChange={percentChange}
    />
  );

  const headerRight = (
    <IconButton size="small" onClick={closeDrawer} aria-label="Close">
      <Close />
    </IconButton>
  );

  const footer = (() => {
    if (editingLotId !== null) {
      return (
        <div className={styles.footer}>
          {saveError !== null && (
            <p className={styles.saveError}>{saveError}</p>
          )}
          <button
            type="button"
            className={styles.deleteButton}
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.addButton}
            onClick={handleSave}
            disabled={isSaving || hasValidationErrors}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      );
    }

    if (isAdding) {
      return (
        <div className={styles.footer}>
          {saveError !== null && (
            <p className={styles.saveError}>{saveError}</p>
          )}
          <button
            type="button"
            className={styles.deleteButton}
            onClick={handleCancelAdd}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.addButton}
            onClick={handleSaveAdd}
            disabled={isSaving || hasValidationErrors}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      );
    }

    if (isConfirmingDelete) {
      return (
        <div className={styles.footer}>
          {saveError !== null && (
            <p className={styles.saveError}>{saveError}</p>
          )}
          <p className={styles.confirmText}>
            Remove this ticker and all its holdings?
          </p>
          <button
            type="button"
            className={styles.deleteButton}
            onClick={handleCancelDelete}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmDeleteButton}
            onClick={handleConfirmDelete}
            disabled={isSaving}
          >
            {isSaving ? "Deleting..." : "Confirm Delete"}
          </button>
        </div>
      );
    }

    return (
      <div className={styles.footer}>
        <button
          type="button"
          className={styles.deleteButton}
          onClick={handleStartDelete}
        >
          Delete
        </button>
        <button
          type="button"
          className={styles.addButton}
          onClick={handleStartAdd}
        >
          Add Lot
        </button>
      </div>
    );
  })();

  const drawerContent = (() => {
    if (isLoading) {
      return <DrawerLoadingSkeleton lotCount={position?.lots.length ?? 2} />;
    }

    if (loadError !== null) {
      return (
        <div className={styles.errorState}>
          <p className={styles.errorStateMessage}>
            There was a network error.
            <br />
            Please try again.
          </p>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={refreshCurrentWatchlist}
          >
            <Refresh aria-hidden="true" />
            Refresh
          </button>
        </div>
      );
    }

    const visibleLots = (position?.lots ?? []).filter(
      (l) => !pendingDeletionIds.has(l.id)
    );

    const hasNoLots =
      !position ||
      position.lots.length === 0 ||
      (position.lots.length === 1 &&
        position.lots[0].shares === 0 &&
        position.lots[0].buyPrice === 0);

    return (
      <div className={styles.content}>
        <DrawerOverview position={position} />
        {isAdding ? (
          <DrawerHistoryTable
            lots={hasNoLots ? [] : visibleLots}
            editingLotId={editingLotId}
            editForm={editForm}
            onEditStart={handleEditStart}
            onFieldChange={handleFieldChange}
            onDeleteLot={handleDeleteLot}
            onValidationChange={setHasValidationErrors}
            isAdding={true}
            addForm={addForm}
            onAddFieldChange={handleAddFieldChange}
            onRemoveAddRow={handleCancelAdd}
          />
        ) : (
          <DrawerHistoryTable
            lots={visibleLots}
            editingLotId={editingLotId}
            editForm={editForm}
            onEditStart={handleEditStart}
            onFieldChange={handleFieldChange}
            onDeleteLot={handleDeleteLot}
            onValidationChange={setHasValidationErrors}
            isAdding={false}
            hasNoLots={hasNoLots}
            onAddLot={handleStartAdd}
          />
        )}
      </div>
    );
  })();

  return (
    <DrawerShell
      isOpen={isOpen}
      onClose={closeDrawer}
      title={title}
      headerRight={headerRight}
      subtitle={subtitle}
      footer={footer}
    >
      {drawerContent}
    </DrawerShell>
  );
}
